import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import Landing from "@/pages/Landing";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import GroomerSignup from "@/pages/GroomerSignup";
import JoinBySlug from "@/pages/JoinBySlug";
import Billing from "@/pages/Billing";
import BillingSuccess from "@/pages/BillingSuccess";
import Admin from "@/pages/Admin";
import GroomerLayout from "@/pages/groomer/GroomerLayout";
import GroomerDashboard from "@/pages/groomer/Dashboard";
import GroomerCalendar from "@/pages/groomer/Calendar";
import GroomerClients from "@/pages/groomer/Clients";
import GroomerServices from "@/pages/groomer/Services";
import GroomerMessages from "@/pages/groomer/Messages";
import GroomerAI from "@/pages/groomer/AIAssistant";
import GroomerRevenue from "@/pages/groomer/Revenue";
import GroomerMap from "@/pages/groomer/MapView";
import PetGallery from "@/pages/PetGallery";
import ShareGallery from "@/pages/ShareGallery";
import ClientLayout from "@/pages/client/ClientLayout";
import ClientHome from "@/pages/client/Home";
import ClientBook from "@/pages/client/Book";
import ClientPets from "@/pages/client/Pets";
import ClientMessages from "@/pages/client/Messages";
import Pay from "@/pages/client/Pay";
import PaymentSuccess from "@/pages/client/PaymentSuccess";
import PaymentCancel from "@/pages/client/PaymentCancel";

function Protected({ role, children }) {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (role === "groomer" && !["groomer", "super_admin"].includes(user.role)) {
    return <Navigate to={user.role === "client" ? "/client" : "/"} replace />;
  }
  if (role && role !== "groomer" && user.role !== role) {
    return <Navigate to={user.role === "client" ? "/client" : "/groomer"} replace />;
  }
  // Subscription gate: real groomers must have active/trialing subscription
  if (user.role === "groomer" &&
      !["active", "trialing"].includes(user.subscription_status || "")) {
    return <Navigate to="/billing" replace />;
  }
  return children;
}
function AppRouter() {
  const location = useLocation();
  // Handle OAuth return synchronously — must be checked BEFORE any route.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<AuthPage mode="login" />} />
      <Route path="/register" element={<AuthPage mode="register" />} />

      <Route path="/groomer" element={<Protected role="groomer"><GroomerLayout /></Protected>}>
        <Route index element={<GroomerDashboard />} />
        <Route path="calendar" element={<GroomerCalendar />} />
        <Route path="clients" element={<GroomerClients />} />
        <Route path="services" element={<GroomerServices />} />
        <Route path="messages" element={<GroomerMessages />} />
        <Route path="ai" element={<GroomerAI />} />
        <Route path="revenue" element={<GroomerRevenue />} />
        <Route path="map" element={<GroomerMap />} />
      </Route>

      <Route path="/pets/:petId/gallery" element={<Protected><PetGallery /></Protected>} />
      <Route path="/share/pets/:token" element={<ShareGallery />} />

      <Route path="/client" element={<Protected role="client"><ClientLayout /></Protected>}>
        <Route index element={<ClientHome />} />
        <Route path="book" element={<ClientBook />} />
        <Route path="pets" element={<ClientPets />} />
        <Route path="messages" element={<ClientMessages />} />
      </Route>

      <Route path="/pay/:appointmentId" element={<Protected role="client"><Pay /></Protected>} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />

      <Route path="/signup/groomer" element={<GroomerSignup />} />
      <Route path="/join/:slug" element={<JoinBySlug />} />
      <Route path="/billing" element={<Billing />} />
      <Route path="/billing/success" element={<BillingSuccess />} />
      <Route path="/admin" element={<Admin />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
